import {
  AdminInput,
  AdminSpinner,
  Button,
  Disclaimer,
  Div,
  H5,
  Icon,
  Img,
  LocationPinIcon,
  ModalPortal,
  P,
  Radio,
  isAdmin,
  lightTheme,
  restaurantImage,
} from '@vgrubs/components';
import 'flatpickr/dist/flatpickr.css';
import debouce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  LocationsPicker,
} from './AdminLocationsPickerStyled';
import { getPartnerLocations } from 'components/src/api/partner/locations.partner-api';
import { getAllVerifiedLocations } from 'components/src/api/locations.api';
import {
  SimpleLocationEntity,
  SimpleLocationWithTodayDataResponse,
} from 'components/src/types/location.types';

export const AdminLocationsPickerModal = ({
  selectedLocations,
  setSelectedLocations,
  handleSubmit,
  label,
  $textCenter,
}: {
  selectedLocations: SimpleLocationEntity[];
  setSelectedLocations: React.Dispatch<
    React.SetStateAction<SimpleLocationEntity[]>
  >;
  handleSubmit: () => void;
  label: string;
  $textCenter: boolean;
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [data, setData] = useState<
    (SimpleLocationEntity | SimpleLocationWithTodayDataResponse)[]
  >([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [ref, inView] = useInView();
  const [hasMore, setHasMore] = useState(true);

  async function getLocations(
    search: boolean,
    page: number,
    hasMore: boolean,
    query: string
  ) {
    if (!hasMore) return;
    try {
      const params = isAdmin
        ? {
            query,
            page,
            limit: 20,
          }
        : {
            query,
            page,
            limit: 20,
            verified: true,
          };

      setLoading(true);
      const getFunction = isAdmin
        ? getAllVerifiedLocations
        : getPartnerLocations;
      const { data } = await getFunction(params);

      if (!search) {
        if (data.data[0]) {
          setHasMore(true);
          setData((prevItems) => [...prevItems, ...data.data]);
        } else {
          setHasMore(false);
        }
      } else {
        if (data.data[0]) {
          setHasMore(true);
          setData(() => [...data.data]);
        } else {
          setData(() => [...data.data]);
          setHasMore(false);
        }
      }

      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      // ErrorToast(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (data.length < 1 && modalIsOpen) {
      getLocations(false, page, hasMore, query);
    }
  }, [modalIsOpen]);

  useEffect(() => {
    if (inView && data.length > 0) {
      getLocations(false, page, hasMore, query);
    }
  }, [inView]);

  const handleSelectLocation = (newItem: SimpleLocationEntity) => {
    const index = selectedLocations.findIndex((item) => item.id === newItem.id);
    if (index === -1) {
      // Item doesn't exist, so add it
      setSelectedLocations((prevItems: SimpleLocationEntity[]) => [
        ...prevItems,
        newItem,
      ]);
    } else {
      // Item exists, so remove it
      setSelectedLocations((prevItems: SimpleLocationEntity[]) =>
        prevItems.filter((item: SimpleLocationEntity) => item.id !== newItem.id)
      );
    }
  };

  function isLocationSelected(item: SimpleLocationEntity) {
    return selectedLocations.some((location) => location.id === item.id);
  }

  function renderLocationInputPlaceholder() {
    if (selectedLocations.length === 0) {
      return 'All Locations';
    } else if (selectedLocations.length === 1) {
      return selectedLocations[0]?.name;
    } else return `${selectedLocations.length} Locations`;
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasMore(true);
    setPage(1);
    setQuery(e.target.value);
    getLocations(true, 1, true, e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debouce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  return (
    <div>
      {label && (
        <P $mb="8" $ml="24" $mr="24" $center={$textCenter}>
          <b>{label}</b>
        </P>
      )}
      <LocationsPicker onClick={() => setModalIsOpen((prev) => !prev)}>
        <Div>{renderLocationInputPlaceholder()}</Div>

        <Icon>
          <LocationPinIcon />
        </Icon>
      </LocationsPicker>
      <ModalPortal
        header="Select Locations"
        overlayBorderRadius={30}
        logo={false}
        isOpen={modalIsOpen}
        closeModal={() => setModalIsOpen(false)}
        modalOverflow="hidden"
        footer={
          <>
            <Button
              $selected
              $gray
              $w="100%"
              $wSM="100%"
              onClick={() => setSelectedLocations([])}
              $mb="16"
            >
              Reset Selection
            </Button>
            <Button
              $selected
              $w="100%"
              $wSM="100%"
              onClick={() => {
                setModalIsOpen((prev) => !prev);
                handleSubmit();
              }}
            >
              Confirm
            </Button>
          </>
        }
        $numOfFloatingButtons={2}
      >
        <Div $relative>
          <Div $mb="20">
            <AdminInput
              search
              onChange={debouncedResults}
              placeholder="Search Location"
            />
          </Div>
          <Div $minHeight="50vh">
            {data.length === 0 && !hasMore && (
              <Div
                $flex
                $center
                $column
                $height="calc(70vh - 300px)"
                $heightSM="calc(60vh - 300px)"
              >
                <Img
                  src={restaurantImage}
                  alt="restaurant"
                  $width="250px"
                  $widthSM="150px"
                />
                <H5>No Locations</H5>
              </Div>
            )}
            {data.map((lc, i) => {
              const l = (
                isAdmin
                  ? lc
                  : (lc as SimpleLocationWithTodayDataResponse).location
              ) as SimpleLocationEntity;
              return (
                <Div
                  key={i}
                  $box
                  $p="14px 24px"
                  $mb="8"
                  $flex
                  $alignCenter
                  $between
                  onClick={() => handleSelectLocation(l)}
                  $gap="16px"
                  $pointer
                  // delay={0.1}
                >
                  <div>
                    <P
                      $left
                      $bold
                      $mb="8"
                      $color={isLocationSelected(l) && lightTheme.primary}
                      $lineHeight="1.2"
                    >
                      {l.name}
                    </P>
                    <Disclaimer $left $mb="0">
                      {l.address}
                    </Disclaimer>
                  </div>

                  <Radio checked={isLocationSelected(l)} />
                </Div>
              );
            })}
            <div ref={ref}></div>
            <Div $height="70px" $heightSM="70px" $relative $mb="-30">
              {loading && (
                <AdminSpinner absolute small $noBackground $noZIndex />
              )}
            </Div>
          </Div>
        </Div>
      </ModalPortal>
    </div>
  );
};
